import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  companyNameApi,
  getCountriesApi,
  getCurrenciesApi,
  getIndustriesApi,
  getJobLevelsApi,
  getLanguagesApi,
  getQualificationTypesApi,
  getSkillsApi,
  getStatesApi,
  getWorkPlacesApi,
  getWorkTypesApi,
} from "../api/master";
import {
  Company,
  CountryItem,
  CurrencyItem,
  IndustryItem,
  JobLevelItem,
  LanguageItem,
  QualificationTypeItem,
  SkillItem,
  StateItem,
  WorkplaceItem,
  WorkTypeItem,
} from "../models/master";

interface JobCreatedContextType {
  createdJob: number;
  setCreatedJob: Dispatch<SetStateAction<number>>;
  openCreateJob: boolean;
  setOpenCreateJob: Dispatch<SetStateAction<boolean>>;
}

interface CompanyContextType {
  company?: Company;
}

interface MasterContextType {
  industries?: IndustryItem[];
  jobLevels?: JobLevelItem[];
  skills?: SkillItem[];
  setSkills: Dispatch<SetStateAction<SkillItem[] | undefined>>;
  currencies?: CurrencyItem[];
  states?: StateItem[];
  languages?: LanguageItem[];
  countries?: CountryItem[];
  workTypes?: WorkTypeItem[];
  workPlaces?: WorkplaceItem[];
  qualificationTypes?: QualificationTypeItem[];
}

export const JobCreatedContext = createContext<JobCreatedContextType>(
  {} as JobCreatedContextType
);

export const CompanyContext = createContext<CompanyContextType>(
  {} as CompanyContextType
);

export const MasterContext = createContext<MasterContextType>(
  {} as MasterContextType
);

const MasterContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [company, setCompany] = useState<Company | undefined>(undefined);

  const [industries, setIndustries] = useState<IndustryItem[] | undefined>();
  const [jobLevels, setJobLevels] = useState<JobLevelItem[] | undefined>();
  const [skills, setSkills] = useState<SkillItem[] | undefined>();
  const [currencies, setCurrencies] = useState<CurrencyItem[] | undefined>();
  const [states, setStates] = useState<StateItem[] | undefined>();
  const [countries, setCountries] = useState<CountryItem[] | undefined>();
  const [languages, setLanguages] = useState<LanguageItem[] | undefined>();
  const [workTypes, setWorkTypes] = useState<WorkTypeItem[] | undefined>();
  const [workPlaces, setWorkPlaces] = useState<WorkplaceItem[] | undefined>();
  const [qualificationTypes, setQualificationTypes] = useState<
    QualificationTypeItem[] | undefined
  >();

  const [createdJob, setCreatedJob] = useState<number>(0);
  const [openCreate, setOpenCreate] = useState<boolean>(false);

  const getCompany = async () => {
    const resp = await companyNameApi();
    if (resp) {
      setCompany(resp.data);
    }
  };

  const getIndustries = async () => {
    const resp = await getIndustriesApi();
    if (resp) {
      setIndustries(resp.data);
    }
  };

  const getSkills = async () => {
    const resp = await getSkillsApi();
    if (resp) {
      setSkills(resp.data);
    }
  };

  const getJobLevels = async () => {
    const resp = await getJobLevelsApi();
    if (resp) {
      setJobLevels(resp.data);
    }
  };

  const getCurrencies = async () => {
    const resp = await getCurrenciesApi();
    if (resp) {
      setCurrencies(resp.data);
    }
  };

  const getStates = async () => {
    const resp = await getStatesApi();
    if (resp) {
      setStates(resp.data);
    }
  };

  const getCountries = async () => {
    const resp = await getCountriesApi();
    if (resp) {
      setCountries(resp.data);
    }
  };

  const getLanguages = async () => {
    const resp = await getLanguagesApi();
    if (resp) {
      setLanguages(resp.data);
    }
  };

  const getWorkTypes = async () => {
    const resp = await getWorkTypesApi();
    if (resp) {
      setWorkTypes(resp.data);
    }
  };

  const getWorkPlaces = async () => {
    const resp = await getWorkPlacesApi();
    if (resp) {
      setWorkPlaces(resp.data);
    }
  };

  const getQualificationTypes = async () => {
    const resp = await getQualificationTypesApi();
    if (resp) {
      setQualificationTypes(resp.data);
    }
  };

  useEffect(() => {
    getCompany();
  }, [createdJob]);

  useEffect(() => {
    getIndustries();
    getJobLevels();
    getSkills();
    getCurrencies();
    getStates();
    getCountries();
    getLanguages();
    getWorkTypes();
    getWorkPlaces();
    getQualificationTypes();
  }, []);

  return (
    <MasterContext.Provider
      value={{
        industries: industries,
        jobLevels: jobLevels,
      
        skills: skills,
        setSkills: setSkills,
        currencies: currencies,
        states: states,
        countries: countries,
        languages: languages,
        workTypes: workTypes,
        workPlaces: workPlaces,
        qualificationTypes: qualificationTypes,
      }}
    >
      <CompanyContext.Provider value={{ company: company }}>
        <JobCreatedContext.Provider
          value={{
            createdJob: createdJob,
            setCreatedJob: setCreatedJob,
            openCreateJob: openCreate,
            setOpenCreateJob: setOpenCreate,
          }}
        >
          {children}
        </JobCreatedContext.Provider>
      </CompanyContext.Provider>
    </MasterContext.Provider>
  );
};

export default MasterContextProvider;

export const useMasterData = () => useContext(MasterContext);
export const useCompanyData = () => useContext(CompanyContext);
export const useCreatedJobData = () => useContext(JobCreatedContext);
